import { create } from 'zustand';
import { useTreeStore } from './treeStore';
import type { SearchScopeKind } from '@/types/domain';
import { EMPTY_SEARCH_FILTERS, type SearchFilters } from '@/types/searchFilters';

export type View = 'tree' | 'quickFile' | 'spaceCreate' | 'settings';

interface UiState {
  view: View;
  modalOpen: string | null;
  /**
   * Terme de recherche PAR ONGLET, et non plus un terme global.
   *
   * POURQUOI. Ouvrir un résultat de recherche crée un nouvel onglet ; avec un
   * terme unique partagé, ce nouvel onglet affichait lui aussi la liste de
   * résultats — le volet ne montrait donc jamais la page qu'on venait d'ouvrir.
   * L'alternative (vider la recherche à l'ouverture) détruisait les résultats de
   * l'onglet d'origine, alors que c'est précisément ce qu'on veut y retrouver en
   * revenant. Chaque onglet porte sa propre recherche, comme dans un navigateur.
   *
   * Volontairement NON persisté : une recherche est un état de session. Les
   * entrées d'onglets fermés sont inertes (les ids d'onglets ne sont jamais
   * réutilisés) et disparaissent au rechargement du volet.
   */
  searchByTab: Record<string, string>;
  /**
   * Filtres de recherche PAR ONGLET, pour la même raison que le terme.
   *
   * ET AVEC UNE RAISON DE PLUS, décisive : depuis qu'un filtre suffit à lancer une
   * recherche (sans mot-clé), des filtres GLOBAUX feraient s'ouvrir un onglet
   * neuf directement sur une liste de résultats, alors que l'utilisateur vient de
   * demander à voir un dossier. Ce qui était une commodité pour le terme devient
   * ici une nécessité.
   */
  filtersByTab: Record<string, SearchFilters>;
  // Portée choisie *explicitement* par l'utilisateur dans le sélecteur, ou `null`
  // pour suivre automatiquement le contexte de navigation (accueil → Partout,
  // page projet → Ce projet, dossier ouvert → Ce dossier ; cf. contextualScope
  // dans useSearch). On ne stocke que le "genre" : les identifiants concrets
  // (projet/dossier) sont résolus depuis l'onglet actif au moment de la requête,
  // pour que "Ce projet" suive le projet affiché. Remis à `null` à chaque
  // changement de contexte de navigation (cf. l'effet dans SearchHeader).
  scopeOverride: SearchScopeKind | null;
  setView: (v: View) => void;
  openModal: (id: string) => void;
  closeModal: () => void;
  /** Écrit le terme de recherche de l'onglet ACTIF. */
  setSearchQuery: (q: string) => void;
  /** Écrit un ou plusieurs filtres de l'onglet ACTIF (fusion partielle). */
  setSearchFilters: (patch: Partial<SearchFilters>) => void;
  /** Remet tous les filtres de l'onglet actif à zéro. */
  clearSearchFilters: () => void;
  setScopeOverride: (s: SearchScopeKind | null) => void;
}

export const useUiStore = create<UiState>((set) => ({
  view: 'tree',
  modalOpen: null,
  searchByTab: {},
  filtersByTab: {},
  scopeOverride: null,
  setView: (view) => set({ view }),
  openModal: (id) => set({ modalOpen: id }),
  closeModal: () => set({ modalOpen: null }),
  setSearchQuery: (q) =>
    set((s) => {
      // L'onglet actif est lu à l'écriture (et non passé en paramètre) pour que
      // tous les points d'appel existants — champ de recherche, croix, Échap —
      // restent inchangés.
      const tabId = useTreeStore.getState().activeTabId;
      if (!tabId) return s;
      return { searchByTab: { ...s.searchByTab, [tabId]: q } };
    }),
  setSearchFilters: (patch) =>
    set((s) => {
      const tabId = useTreeStore.getState().activeTabId;
      if (!tabId) return s;
      const current = s.filtersByTab[tabId] ?? EMPTY_SEARCH_FILTERS;
      return { filtersByTab: { ...s.filtersByTab, [tabId]: { ...current, ...patch } } };
    }),
  clearSearchFilters: () =>
    set((s) => {
      const tabId = useTreeStore.getState().activeTabId;
      if (!tabId) return s;
      return { filtersByTab: { ...s.filtersByTab, [tabId]: EMPTY_SEARCH_FILTERS } };
    }),
  setScopeOverride: (scopeOverride) => set({ scopeOverride }),
}));

/**
 * Terme de recherche de l'onglet actif.
 *
 * Hook et non sélecteur simple : il faut s'abonner AUX DEUX stores (l'onglet
 * actif change dans `treeStore`, le terme dans `uiStore`), sinon changer d'onglet
 * n'actualiserait pas l'affichage.
 */
export function useSearchQuery(): string {
  const tabId = useTreeStore((s) => s.activeTabId);
  return useUiStore((s) => (tabId ? (s.searchByTab[tabId] ?? '') : ''));
}

/**
 * Filtres de l'onglet actif. Même construction que `useSearchQuery`, et pour la
 * même raison : l'abonnement doit porter sur les DEUX stores.
 *
 * Rend la constante partagée `EMPTY_SEARCH_FILTERS` quand l'onglet n'a rien
 * saisi — une référence STABLE, là où un `{...}` fabriqué à la lecture ferait
 * croire à zustand que l'état a changé à chaque rendu (boucle de re-rendu).
 */
export function useSearchFilters(): SearchFilters {
  const tabId = useTreeStore((s) => s.activeTabId);
  return useUiStore((s) =>
    tabId ? (s.filtersByTab[tabId] ?? EMPTY_SEARCH_FILTERS) : EMPTY_SEARCH_FILTERS,
  );
}
